'use client'

import React, { useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { FollowUpOption } from '@/utils/ai/types'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface FollowUpButtonsProps {
  options: FollowUpOption[]
  onSelect: (option: FollowUpOption, customInput?: string) => void
  allowCustomInput?: boolean
  category: string
  isLoading?: boolean
}

const FollowUpButtons: React.FC<FollowUpButtonsProps> = ({
  options,
  onSelect,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  allowCustomInput: _allowCustomInput = true, // Reserved for future custom input feature
  category,
  isLoading = false
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
    }
  }

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 272 // Width of one option card + gap (w-64 + gap-3 = 256px + 12px)
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount)
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      })
    }
  }

  React.useEffect(() => {
    checkScrollButtons()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollButtons)
      return () => container.removeEventListener('scroll', checkScrollButtons)
    }
  }, [options])

  if (isLoading || options.length === 0) {
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
      className="mt-0"
    >
      {/* Mobile-optimized horizontal carousel */}
      <div className="relative">
        {/* Scroll buttons - only show on mobile when needed */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
        )}

        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-white transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        )}

        {/* Combined Layout: Wrap-around chips (similar to target design) */}
        <div className="flex flex-wrap gap-2 pb-2">
          {options.map((option, index) => (
            <motion.button
              key={option.id}
              onClick={() => onSelect(option)}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{
                scale: 1.03,
                backgroundColor: '#4a7c5e',
                color: '#ffffff',
                borderColor: '#4a7c5e'
              }}
              whileTap={{ scale: 0.97 }}
              className="px-4 py-2.5 rounded-full border-2 transition-all duration-200 text-sm font-semibold whitespace-nowrap"
              style={{
                background: 'white',
                borderColor: '#b8d9c4',
                color: '#2d5a3d',
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                fontFamily: "'Source Serif 4', serif"
              }}
            >
              {option.text}
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

export default FollowUpButtons
